import React from 'react';
import { ArrowLeft, Heart, Package, Truck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Template 13: CRAFT - Artisan/Handmade, Warm & Natural (Inspired by Shopify's Craft theme)
const Template13 = () => {
    const navigate = useNavigate();

    return (
        <div className="bg-amber-50 min-h-screen font-serif">
            {/* Warm, Natural Header */}
            <header className="bg-white border-b-2 border-amber-200">
                <div className="max-w-6xl mx-auto px-6 py-4">
                    <div className="flex justify-between items-center">
                        <button
                            onClick={() => navigate('/TemplateThemes')}
                            className="flex items-center gap-2 text-sm text-amber-900 hover:text-amber-700 transition-colors"
                        >
                            <ArrowLeft size={16} /> Back
                        </button>

                        <h1 className="text-3xl font-bold text-amber-900">Craft & Co.</h1>

                        <div className="flex gap-4">
                            <button className="text-amber-900 hover:text-amber-700 transition-colors">
                                <Heart size={22} />
                            </button>
                            <button className="text-amber-900 hover:text-amber-700 transition-colors">
                                <Package size={22} />
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Hero - Artisan Story */}
            <section className="relative bg-white">
                <div className="max-w-6xl mx-auto px-6 py-20">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                        <div>
                            <p className="text-sm text-amber-600 mb-4 tracking-wide uppercase">Handcrafted with Love</p>
                            <h2 className="text-5xl md:text-6xl font-bold text-amber-950 mb-6 leading-tight">
                                Made by Hand,<br/>Made with Heart
                            </h2>
                            <p className="text-lg text-amber-800 mb-8 leading-relaxed">
                                Each piece is carefully crafted by skilled artisans using traditional techniques
                                passed down through generations. Discover unique creations that tell a story.
                            </p>
                            <button className="px-8 py-4 bg-amber-900 text-white font-medium hover:bg-amber-800 transition-colors rounded-sm">
                                Shop Collection
                            </button>
                        </div>
                        <div className="relative">
                            <img
                                src="https://images.unsplash.com/photo-1452860606245-08befc0ff44b?w=800&h=600&fit=crop"
                                alt="Artisan Craft"
                                className="w-full h-[500px] object-cover rounded-sm shadow-xl"
                            />
                            <div className="absolute -bottom-6 -left-6 bg-white p-6 shadow-lg rounded-sm max-w-xs">
                                <p className="text-sm text-amber-900 font-medium">
                                    "Every stitch, every detail is a labor of love." - Master Artisan
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Categories */}
            <section className="max-w-6xl mx-auto px-6 py-20">
                <div className="text-center mb-12">
                    <h2 className="text-4xl font-bold text-amber-950 mb-4">Our Collections</h2>
                    <p className="text-lg text-amber-700">Handpicked selections of artisan goods</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { title: 'Pottery', image: 'https://images.unsplash.com/photo-1493106641515-6b5631de4bb9?w=600&h=800&fit=crop', desc: 'Hand-thrown ceramics' },
                        { title: 'Textiles', image: 'https://images.unsplash.com/photo-1558769132-cb1aea6c43e4?w=600&h=800&fit=crop', desc: 'Woven with tradition' },
                        { title: 'Woodwork', image: 'https://images.unsplash.com/photo-1533090161767-e6ffed986c88?w=600&h=800&fit=crop', desc: 'Carved to perfection' }
                    ].map((cat, index) => (
                        <div key={index} className="group cursor-pointer bg-white rounded-sm overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                            <div className="relative overflow-hidden aspect-[3/4]">
                                <img
                                    src={cat.image}
                                    alt={cat.title}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                            </div>
                            <div className="p-6 text-center">
                                <h3 className="text-2xl font-bold text-amber-950 mb-2">{cat.title}</h3>
                                <p className="text-amber-700 mb-4">{cat.desc}</p>
                                <button className="text-amber-900 font-medium border-b-2 border-amber-900 hover:border-amber-700 transition-colors">
                                    Browse Collection
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Products Grid - Natural Feel */}
            <section className="bg-white py-20">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold text-amber-950 mb-4">Featured Pieces</h2>
                        <p className="text-lg text-amber-700">One-of-a-kind artisan creations</p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((id) => (
                            <div key={id} className="group bg-amber-50 rounded-sm overflow-hidden hover:shadow-lg transition-shadow">
                                <div className="relative aspect-square overflow-hidden">
                                    <img
                                        src={`https://images.unsplash.com/photo-${1517487806991 + id * 30000}-93ed2985cad2?w=500&h=500&fit=crop`}
                                        alt={`Product ${id}`}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                    <div className="absolute top-3 right-3">
                                        <button className="p-2 bg-white/90 rounded-full hover:bg-white transition-colors">
                                            <Heart size={16} className="text-amber-900" />
                                        </button>
                                    </div>
                                </div>
                                <div className="p-4 text-center">
                                    <h3 className="text-sm font-semibold text-amber-950 mb-1">Handcrafted Item {id}</h3>
                                    <p className="text-xs text-amber-600 mb-2">By Local Artisan</p>
                                    <p className="text-lg font-bold text-amber-900">$XX.00</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Promise - Values */}
            <section className="max-w-6xl mx-auto px-6 py-20">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                    {[
                        { icon: Heart, title: 'Handmade', desc: 'Crafted with care by skilled artisans' },
                        { icon: Truck, title: 'Fair Trade', desc: 'Supporting communities worldwide' },
                        { icon: Package, title: 'Sustainable', desc: 'Eco-friendly materials and practices' }
                    ].map((item, index) => (
                        <div key={index} className="text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-900 text-white rounded-full mb-4">
                                <item.icon size={28} />
                            </div>
                            <h3 className="text-xl font-bold text-amber-950 mb-3">{item.title}</h3>
                            <p className="text-amber-700">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Artisan Story */}
            <section className="bg-amber-100 py-20">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <img
                        src="https://images.unsplash.com/photo-1556910110-a6d1f8e1e8b5?w=1200&h=400&fit=crop"
                        alt="Workshop"
                        className="w-full h-64 object-cover rounded-sm mb-8 shadow-lg"
                    />
                    <h2 className="text-4xl font-bold text-amber-950 mb-6">The Craft Behind the Product</h2>
                    <p className="text-lg text-amber-800 leading-relaxed max-w-2xl mx-auto">
                        Our artisans use time-honored techniques combined with modern creativity to produce
                        pieces that are not just products, but works of art. Each item carries the maker's
                        signature and tells its own unique story.
                    </p>
                </div>
            </section>

            {/* Newsletter */}
            <section className="bg-white py-16">
                <div className="max-w-2xl mx-auto px-6 text-center">
                    <h3 className="text-3xl font-bold text-amber-950 mb-4">Join Our Craft Community</h3>
                    <p className="text-amber-700 mb-8">Get exclusive access to new collections and artisan stories</p>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-6 py-3 border-2 border-amber-200 rounded-sm focus:outline-none focus:border-amber-900 text-amber-950"
                        />
                        <button className="px-8 py-3 bg-amber-900 text-white font-medium hover:bg-amber-800 transition-colors rounded-sm">
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-amber-900 text-amber-100 py-12">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <h3 className="text-2xl font-bold mb-4">Craft & Co.</h3>
                            <p className="text-amber-200 text-sm">
                                Supporting artisans and preserving traditional crafts since 2020.
                            </p>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4">Shop</h4>
                            <ul className="space-y-2 text-sm text-amber-200">
                                <li><a href="#" className="hover:text-white transition-colors">New Arrivals</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Collections</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Artisans</a></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4">About</h4>
                            <ul className="space-y-2 text-sm text-amber-200">
                                <li><a href="#" className="hover:text-white transition-colors">Our Story</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Fair Trade</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Sustainability</a></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4">Help</h4>
                            <ul className="space-y-2 text-sm text-amber-200">
                                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Shipping</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Returns</a></li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-amber-800 pt-6 text-center text-sm text-amber-200">
                        <p>&copy; 2025 Craft & Co. Made with love by artisans worldwide.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template13;
